import React from "react";
import CopyButton from "@/components/CopyButton";

const JOB_POST = `TITLE: Build an AI [lead intake / follow-up / booking] system using existing
platforms (no custom code)

DESCRIPTION:
I need an experienced automation builder to assemble a working AI system
from existing platforms. This is assembly and configuration, not software
development.

WHAT IT DOES (plain English):
- Responds to inbound [chats/texts/calls] within 60 seconds
- Asks 4 to 6 qualification questions (I will provide the exact logic)
- Books qualified prospects into a calendar
- Logs every conversation to a simple CRM or spreadsheet
- Hands off to a human when [my rule]

DELIVERABLES:
1. The working system, live and tested end-to-end
2. A settings document: every account, login structure, and configuration,
   written so a non-technical person can maintain it
3. A 30-minute recorded walkthrough of how it works and how to duplicate it
   for a new client
4. Two weeks of bug-fix support after delivery

REQUIREMENTS:
- Show me 2 examples of similar systems you've built (screenshots or video)
- All accounts created under MY ownership; I retain full rights to the
  system, templates, and documentation
- Fixed price with 3 milestones: 30% design approved / 40% working demo /
  30% delivery and walkthrough

Reply with: your 2 examples, your platform stack of choice, your fixed
price, and your timeline. Applications without examples are ignored.`;

const OWNERSHIP = `All work product, including system configurations, prompt templates,
workflows, documentation, and any accounts created for this project, is
work made for hire and the exclusive property of [YOUR NAME/COMPANY].
Contractor retains no rights, access, or license after final payment.
All credentials transfer to Client at delivery.`;

const INSTANT_REJECT = [
  "No relevant examples attached",
  "Proposes custom code for something platforms already do",
  "Hourly-only pricing",
  "Copy-paste proposal that doesn't mention your use case",
  "Timeline over 3 weeks for a first build",
];
const GREEN_FLAGS = [
  "Asks a smart question about your qualification logic or handoff rule",
  "Names a specific, sensible platform stack and why",
  "Mentions documentation or handover without being asked",
  "Portfolio shows the same INDUSTRY or same FUNCTION as your offer",
];
const INTERVIEW = [
  ["\"Walk me through the last system like this you built. What broke after launch?\"", "(Honest builders have an answer. Pretenders say nothing broke.)"],
  ["\"If the AI gets a question it can't handle, what happens?\"", "(You want: graceful handoff to human plus logging. Not: 'it won't happen.')"],
  ["\"How will I duplicate this for my second client without you?\"", "(Tests whether they build for your independence or your dependence.)"],
  ["\"What do the platform subscriptions cost ME monthly to run this?\"", "(You need this number for your own margin math. Expect $50 to $200/mo.)"],
  ["\"What do you need from me, and by when?\"", "(Good builders give you a short, specific list.)"],
  ["\"What would you cut from my spec to hit the deadline?\"", "(Tests judgment. Scope discipline beats feature ambition.)"],
  ["\"Fixed price, three milestones, I own everything. Any issue with that?\"", "(Any hesitation on ownership is a no-hire.)"],
];

const CodeBlock = ({ label, text, testId }) => (
  <div style={{ border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden", marginTop: 8 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f2f3f0", borderBottom: "1px solid var(--line)" }}>
      <span className="eyebrow">{label}</span>
      <CopyButton text={text} testId={testId} />
    </div>
    <pre style={{ margin: 0, padding: 14, fontFamily: "IBM Plex Mono, monospace", fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{text}</pre>
  </div>
);

export default function HiringKit() {
  return (
    <div className="prose-weekend" data-testid="asset-body-hiring">
      <p style={{ color: "var(--muted)" }}>Get Your Delivery System Built Once, Cheap, and Owned by You</p>
      <p style={{ fontSize: 15 }}>How to use this: Sunday morning. Your One-Page Offer Doc (Asset 04) is the spec. This kit turns it into a hire. Budget expectation for a first build: $200 to $800 on platforms like Upwork or Fiverr, depending on channels and integrations.</p>

      <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, marginTop: 22 }}>PART 1: The Job Post Template</h3>
      <p style={{ fontSize: 15 }}>Post on Upwork (best filtering) or Fiverr Pro. Fixed price, milestones, not hourly.</p>
      <CodeBlock label="Job post template" text={JOB_POST} testId="hiring-jobpost-copy" />

      <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, marginTop: 22 }}>PART 2: Filtering Criteria (Cut 90% in 5 Minutes)</h3>
      <p style={{ fontSize: 15 }}><strong>Instant rejections:</strong></p>
      <ul>{INSTANT_REJECT.map((x, i) => <li key={i} style={{ margin: "4px 0" }}>{x}</li>)}</ul>
      <p style={{ fontSize: 15 }}><strong>Green flags:</strong></p>
      <ul>{GREEN_FLAGS.map((x, i) => <li key={i} style={{ margin: "4px 0" }}>{x}</li>)}</ul>
      <p style={{ fontSize: 15, fontWeight: 600 }}>Shortlist 3. Interview 2. Hire 1.</p>

      <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, marginTop: 22 }}>PART 3: The Interview (15 Minutes, These 7 Questions)</h3>
      <ol style={{ paddingLeft: 20 }}>
        {INTERVIEW.map(([q, note], i) => (
          <li key={i} style={{ margin: "10px 0" }}>{q} <span style={{ color: "var(--muted)" }}>{note}</span></li>
        ))}
      </ol>

      <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, marginTop: 22 }}>PART 4: The Ownership Clause (Paste Into Your Contract/Offer)</h3>
      <CodeBlock label="Ownership clause" text={OWNERSHIP} testId="hiring-ownership-copy" />
      <p style={{ fontSize: 14, color: "var(--muted)", marginTop: 8 }}>(Standard language for this situation; have any contract reviewed by a professional if the engagement is substantial.)</p>

      <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, marginTop: 22 }}>PART 5: Parallel Path Reminder</h3>
      <p style={{ fontSize: 15 }}>Do NOT wait for the build to sell. Sunday night you open outreach (Asset 08) while the build runs. If a prospect closes before the system is live, congratulations: their onboarding call becomes your requirements call, and your freelancer builds against a real client. Founders have done this forever. It's called selling, then building.</p>
    </div>
  );
}
